import {
   AppointmentDetails,
   AppointmentDetailsQuery,
   AppointmentPractitioner,
   PractitionerAbleToCompleteAppointmentQuery,
} from '@dreryk/egabinet-appointment-frontend-api';
import {
   HealthcareProviderLocation,
   HealthcareProviderLocationQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { PatientSimple, PatientSimpleQuery } from '@dreryk/egabinet-patient-frontend-api';
import { WaitlistEntry, WaitlistEntryQuery } from '@dreryk/egabinet-waitlist-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';

import { useCqrsFind, useCqrsGet, useCqrsSearch } from '@dreryk/command-query-react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { appointmentsTranslationKeys } from '../../../../translations';

type AppointmentWaitlistUpdateFormLoaderRenderProps = {
   appointmentDetails: AppointmentDetails;
   practitioners: AppointmentPractitioner[];
   healthcareProviderLocations: HealthcareProviderLocation[];
   patient: PatientSimple;
   waitlistEntry: WaitlistEntry;
};

type AppointmentWaitlistUpdateFormLoaderRenderer = (
   result: AppointmentWaitlistUpdateFormLoaderRenderProps
) => JSX.Element;

type AppointmentWaitlistUpdateFormLoaderProps = {
   readonly appointmentId: number;
   readonly children: AppointmentWaitlistUpdateFormLoaderRenderer;
};

// eslint-disable-next-line max-lines-per-function
export const AppointmentWaitlistUpdateFormLoader = (props: AppointmentWaitlistUpdateFormLoaderProps): JSX.Element => {
   const {
      data: practitioners,
      loading: practitionersLoading,
      error: practitionersError,
   } = useCqrsSearch<AppointmentPractitioner>(() => new PractitionerAbleToCompleteAppointmentQuery());

   const {
      data: healthcareProviderLocations,
      loading: healthcareProviderLocationsLoading,
      error: healthcareProviderLocationsError,
   } = useCqrsSearch<HealthcareProviderLocation>(() => new HealthcareProviderLocationQuery());

   const {
      data: appointmentDetails,
      loading: appointmentDetailsLoading,
      error: appointmentDetailsError,
   } = useCqrsGet<AppointmentDetails>(() => {
      const query = new AppointmentDetailsQuery();
      query.appointmentId = AppointmentId.of(props.appointmentId);
      return query;
   });

   const { data: patient, loading: patientLoading, error: patientError } = useCqrsGet<PatientSimple>(
      () => {
         const query = new PatientSimpleQuery();
         if (appointmentDetails) {
            query.id = Slot.of(appointmentDetails.patient.id);
         }
         return query;
      },
      {
         enabled: Boolean(appointmentDetails),
      }
   );

   const {
      data: waitlistEntry,
      loading: waitlistEntryLoading,
      error: waitlistEntryError,
   } = useCqrsFind<WaitlistEntry | null>(() => {
      const query = new WaitlistEntryQuery();
      query.appointmentId = Slot.of(AppointmentId.of(props.appointmentId));
      return query;
   });

   if (
      practitionersLoading ||
      healthcareProviderLocationsLoading ||
      appointmentDetailsLoading ||
      patientLoading ||
      waitlistEntryLoading
   ) {
      return <LoaderSpinner />;
   }

   if (
      practitionersError ||
      healthcareProviderLocationsError ||
      appointmentDetailsError ||
      patientError ||
      waitlistEntryError ||
      !waitlistEntry
   ) {
      return (
         <Message
            testId={'appointmentDetailsFetchError'}
            type={MessageType.NEGATIVE}
            label={appointmentsTranslationKeys.update.form.errors.fetchData}
            displayIndicator
         />
      );
   }

   return props.children({
      appointmentDetails: appointmentDetails,
      practitioners: practitioners,
      healthcareProviderLocations: healthcareProviderLocations,
      patient: patient,
      waitlistEntry: waitlistEntry,
   });
};
